import { useQuery } from "@tanstack/react-query";
import {
  LabVisitsService,
  type GetApiAdminLabVisitsByIdWearableDeltasResponse,
} from "@/lib/api_client";
import { useAuth } from "@/lib/auth";
import { impersonateHeaders } from "@/lib/api";

/** The structured wearable section as returned by
 *  GET /api/admin/lab-visits/:id/wearable-deltas. Null when the section is
 *  omitted (no wearable connected or the now-slice is too sparse). */
export type WearableDeltasSection = NonNullable<
  GetApiAdminLabVisitsByIdWearableDeltasResponse["section"]
>;

// Answers only an impersonation token. The caller mounts under the anamnesis
// page's impersonation gate, as useConsultationNotes does.
export const useWearableDeltas = (labVisitId: string | null | undefined) => {
  const { impersonateToken } = useAuth();
  return useQuery<WearableDeltasSection | null>({
    queryKey: ["wearable-deltas", labVisitId],
    queryFn: async () => {
      const res = await LabVisitsService.getApiAdminLabVisitsByIdWearableDeltas({
        path: { id: labVisitId! },
        headers: impersonateHeaders(impersonateToken!),
      });
      // hey-api types the response body inconsistently for nested-object 200s;
      // cast and unwrap the {section: ...} envelope. Null is a valid value
      // (section omitted: no wearable connected or now-slice too sparse).
      const body = res.data as unknown as {
        section: WearableDeltasSection | null;
      } | undefined;
      return body?.section ?? null;
    },
    enabled: !!impersonateToken && !!labVisitId,
    staleTime: 60_000,
  });
};
